import { useEffect, useRef } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const POLL_MS = 15_000; // 15s

/**
 * Polls /api/proposals/opens/feed to alert the rep (and OM/admin) whenever
 * a prospect opens their proposal. Renders a big sonner toast.
 */
export function useProposalOpens() {
  const { user } = useAuth();
  const sinceRef = useRef(null);

  useEffect(() => {
    if (!user || user.must_change_password) return;
    if (!["admin", "rep", "office_manager"].includes(user.role)) return;

    let cancelled = false;

    (async () => {
      try {
        const { data } = await api.get("/proposals/opens/feed", { params: { limit: 1 } });
        sinceRef.current = data?.server_time || new Date().toISOString();
      } catch (e) {
        sinceRef.current = new Date().toISOString();
      }
    })();

    const tick = async () => {
      if (cancelled || !sinceRef.current) return;
      try {
        const { data } = await api.get("/proposals/opens/feed", { params: { since: sinceRef.current } });
        const opens = data?.opens || [];
        for (const op of opens) {
          const isMine = user.role === "rep" ? op.assigned_rep_id === user.id : true;
          const title = isMine
            ? `${op.business_name || "A prospect"} just opened your proposal`
            : `${op.business_name || "A prospect"} opened a proposal`;
          const desc = "Call them now — they're on the page.";
          toast.success(title, {
            description: desc,
            duration: 15000,
            id: `proposal-open-${op.id}`,
          });
        }
        if (opens.length > 0) {
          sinceRef.current = opens[opens.length - 1].opened_at;
        } else if (data?.server_time) {
          sinceRef.current = data.server_time;
        }
      } catch (e) {
        // silent
      }
    };

    const timer = setInterval(tick, POLL_MS);
    const onVis = () => {
      if (document.visibilityState === "visible") tick();
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      cancelled = true;
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [user]);
}
